import {
	CircularProgress,
	Paper,
	Stack,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	DeleteIcon,
	EditIcon,
	EyeIcon,
	RecentChatIcon,
} from '../../../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../../../assets/theme/Colors';
import FacebookActionIconButton from './FacebookActionIconButton';
import FacebookMediaList, { TableRowText } from './FacebookMediaList';
import { FacebookPost } from '../../../types';

interface FacebookPostsTableProps {
	posts: Array<FacebookPost>;
	onViewPost: (post: FacebookPost) => void;
	onEditPost: (post: FacebookPost) => void;
	onDeletePost: (post: FacebookPost) => void;
	onViewChats: (post: FacebookPost) => void;
	isActionLoading?: boolean;
	loadingChatPostId?: string;
}

function FacebookPostsTable({
	posts,
	onViewPost,
	onEditPost,
	onDeletePost,
	onViewChats,
	isActionLoading = false,
	loadingChatPostId = '',
}: FacebookPostsTableProps) {
	return (
		<TableContainer
			component={Paper}
			sx={{ boxShadow: 'none', overflow: 'visible' }}>
			<Table
				stickyHeader
				sx={{ minWidth: 1050, width: '100%' }}
				aria-label="facebook posts table">
				<TableHead>
					<TableRow>
						<StyledTableCell align="center">
							<TableHeaderText>ID</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Post URL</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Name</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Description</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Images/PDFs</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Likes</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>
								Welcome Message (EN)
							</TableHeaderText>
						</StyledTableCell>
						<StyledTableCell align="center">
							<TableHeaderText>Actions</TableHeaderText>
						</StyledTableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{posts.length > 0 ? (
						posts.map(post => {
							const isInactivePost = post.active === false;

							return (
								<StyledTableRow
									key={post.id}
									inactive={isInactivePost ? 1 : 0}>
									<StyledTableCell align="center">
										<TableRowText>
											{post.source_id}
										</TableRowText>
									</StyledTableCell>
									<StyledTableCell align="center">
										<ScrollableCell>
											{post.postUrl ? (
												<PostLink
													href={post.postUrl}
													target="_blank"
													rel="noreferrer">
													{post.postUrl}
												</PostLink>
											) : (
												<TableRowText>-</TableRowText>
											)}
										</ScrollableCell>
									</StyledTableCell>
									<StyledTableCell align="center">
										<ScrollableCell>
											<TableRowText>
												{post.name}
											</TableRowText>
										</ScrollableCell>
									</StyledTableCell>
									<StyledTableCell align="center">
										<ScrollableCell>
											<TableRowText>
												{post.description}
											</TableRowText>
										</ScrollableCell>
									</StyledTableCell>
									<StyledTableCell align="center">
										<FacebookMediaList
											media={post.media}
											scrollable
										/>
									</StyledTableCell>
									<StyledTableCell align="center">
										<TableRowText>
											{post.likes ?? '-'}
										</TableRowText>
									</StyledTableCell>
									<StyledTableCell align="center">
										<ScrollableCell>
											<TableRowText>
												{post.welcomeMessages
													?.english || '-'}
											</TableRowText>
										</ScrollableCell>
									</StyledTableCell>
									<StyledTableCell align="center">
										<Stack
											direction="row"
											justifyContent="center"
											spacing={1}>
											<Tooltip title="View">
												<FacebookActionIconButton
													onClick={() =>
														onViewPost(post)
													}>
													<EyeIcon
														color={MAIN_THEME_COLOR}
													/>
												</FacebookActionIconButton>
											</Tooltip>
											<Tooltip title="Edit">
												<FacebookActionIconButton
													disabled={isActionLoading}
													onClick={() =>
														onEditPost(post)
													}>
													<EditIcon />
												</FacebookActionIconButton>
											</Tooltip>
											<Tooltip title="Chats">
												<FacebookActionIconButton
													disabled={isActionLoading}
													onClick={() =>
														onViewChats(post)
													}>
													{loadingChatPostId ===
													post.id ? (
														<CircularProgress
															size={16}
															sx={{
																color: MAIN_THEME_COLOR,
															}}
														/>
													) : (
														<RecentChatIcon
															color={
																MAIN_THEME_COLOR
															}
														/>
													)}
												</FacebookActionIconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<FacebookActionIconButton
													disabled={isActionLoading}
													onClick={() =>
														onDeletePost(post)
													}>
													<DeleteIcon />
												</FacebookActionIconButton>
											</Tooltip>
										</Stack>
									</StyledTableCell>
								</StyledTableRow>
							);
						})
					) : (
						<StyledTableRow>
							<StyledTableCell colSpan={8} align="center">
								<TableRowText>
									No Facebook posts available
								</TableRowText>
							</StyledTableCell>
						</StyledTableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
		position: 'sticky',
		top: 0,
		zIndex: 3,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		maxWidth: 200,
		height: '72px',
		verticalAlign: 'middle',
		padding: '8px 12px',
	},
}));

const StyledTableRow = styled(TableRow, {
	shouldForwardProp: prop => prop !== 'inactive',
})<{ inactive?: number }>(({ inactive }) => ({
	height: '72px',
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	...(inactive && {
		background: '#EFF6FF',
		boxShadow: `inset 4px 0 0 ${MAIN_THEME_COLOR}`,
		'&:nth-of-type(odd)': {
			background: '#EFF6FF',
		},
		'& td': {
			borderBottomColor: '#BFDBFE',
		},
	}),
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

const ScrollableCell = styled('div')(() => ({
	maxHeight: '56px',
	overflowY: 'auto',
	textAlign: 'left',
	'&::-webkit-scrollbar': {
		width: '3px',
	},
	'&::-webkit-scrollbar-track': {
		background: 'transparent',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#D1D5DB',
		borderRadius: '2px',
	},
}));

const TableHeaderText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '600',
	color: 'rgba(0, 0, 0, 0.5)',
}));

const PostLink = styled('a')(() => ({
	color: MAIN_THEME_COLOR,
	fontSize: '12px',
	fontWeight: 400,
	textDecoration: 'underline',
	wordBreak: 'break-all',
}));

export default FacebookPostsTable;
